import React, { useState } from 'react';
import { BaseConfigProps, commonStyles, conditionOperators } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const MultiConditionConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['conditions', 'logic']));
  
  const conditions = node.data.config.conditions || [];
  const logicOperator = node.data.config.operator || 'AND';

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const addCondition = () => {
    const newConditions = [...conditions, { field: '', operator: '==', value: '' }];
    updateConfig('conditions', newConditions);
  };

  const updateCondition = (index: number, key: string, value: any) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [key]: value };
    updateConfig('conditions', newConditions);
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_: any, i: number) => i !== index);
    updateConfig('conditions', newConditions);
  };

  return (
    <div className="space-y-3">
      {/* Logikai Operátor */}
      <CollapsibleSection
        id="logic"
        title="Logikai Operátor"
        icon="🔗"
        color="blue"
        isExpanded={expandedSections.has('logic')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.smallLabel}>
              Feltételek közötti kapcsolat
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'AND', label: 'ÉS (AND)', desc: 'Minden feltételnek teljesülnie kell', icon: '∧' },
                { value: 'OR', label: 'VAGY (OR)', desc: 'Legalább egy feltételnek teljesülnie kell', icon: '∨' }
              ].map((op) => (
                <button
                  key={op.value}
                  onClick={() => updateConfig('operator', op.value)}
                  className={`flex flex-col items-center justify-center p-3 rounded-md border text-sm transition-all min-h-[80px] ${
                    logicOperator === op.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  <span className="text-2xl mb-1">{op.icon}</span>
                  <span className="font-medium text-xs text-center leading-tight">{op.label}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">{op.desc}</span>
                  {logicOperator === op.value && (
                    <span className="text-blue-500 mt-1">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Feltételek */}
      <CollapsibleSection
        id="conditions"
        title={`Feltételek (${conditions.length})`}
        icon="🎯"
        color="purple"
        isExpanded={expandedSections.has('conditions')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          {conditions.length === 0 ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto mb-2 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <span className="text-2xl">🎯</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Még nincsenek feltételek megadva
              </p>
              <button
                onClick={addCondition}
                className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 transition-colors"
              >
                + Első feltétel hozzáadása
              </button>
            </div>
          ) : (
            <>
              {conditions.map((condition: any, index: number) => (
                <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Feltétel #{index + 1}
                    </span>
                    <button
                      onClick={() => removeCondition(index)}
                      className="text-red-500 hover:text-red-700 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    {/* Mező */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Mező
                      </label>
                      <input
                        type="text"
                        value={condition.field || ''}
                        onChange={(e) => updateCondition(index, 'field', e.target.value)}
                        className={commonStyles.input}
                        placeholder="pl. amount"
                      />
                    </div>
                    
                    {/* Operátor */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Operátor
                      </label>
                      <select
                        value={condition.operator || '=='}
                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                        className={commonStyles.input}
                      >
                        {conditionOperators.map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Érték */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Érték
                      </label>
                      <input
                        type="text"
                        value={condition.value || ''}
                        onChange={(e) => updateCondition(index, 'value', e.target.value)}
                        className={commonStyles.input}
                        placeholder="pl. 1000"
                        disabled={condition.operator === 'isEmpty' || condition.operator === 'isNotEmpty'}
                      />
                    </div>
                  </div>
                  
                  {/* Változó segítség */}
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    💡 Használj változókat: <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{`{{változó_név}}`}</code>
                  </div>
                </div>
              ))}
              
              <button
                onClick={addCondition}
                className="w-full px-3 py-2 border-2 border-dashed border-purple-300 dark:border-purple-600 text-purple-600 dark:text-purple-400 text-xs rounded-md hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
              >
                + Újabb feltétel hozzáadása
              </button>
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* Előnézet */}
      {conditions.length > 0 && (
        <CollapsibleSection
          id="preview"
          title="Feltétel Előnézet"
          icon="👁️"
          color="green"
          isExpanded={expandedSections.has('preview')}
          onToggle={toggleSection}
        >
          <div className={commonStyles.codeBlock}>
            <div className="text-green-400 font-mono text-xs">
              {conditions.map((condition: any, index: number) => (
                <div key={index} className="flex items-center space-x-2">
                  {index > 0 && (
                    <span className="text-yellow-400 font-bold">{logicOperator}</span>
                  )}
                  <span className="text-blue-400">{condition.field || 'mező'}</span>
                  <span className="text-red-400">{condition.operator || '=='}</span>
                  {condition.operator !== 'isEmpty' && condition.operator !== 'isNotEmpty' && (
                    <span className="text-green-400">"{condition.value || 'érték'}"</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default MultiConditionConfig;
